<style lang="postcss">
</style>

<script lang="ts">
	import IMask from "imask";
	import FieldError from "$form/Fields/Shared/FieldError.svelte";
	import { onMount, onDestroy } from "svelte";
	import { BROWSER } from "esm-env";
	import { createEventDispatcher } from "svelte";
	import {
		autoUpdate,
		computePosition,
		flip,
		shift,
		offset
	} from "@floating-ui/dom";
	import dayjs from "dayjs";
	import { getContext } from "svelte";
	import { portal } from "design/helpers/svelte-portal";

	import Label from "$form/Fields/Shared/Label.svelte";
	import Description from "$form/Fields/Shared/Description.svelte";
	import Input from "$form/Fields/Shared/Input.svelte";

	export let field: FieldDisplay & { type: "Date" };
	export let value: string;

	const suffixIcon: Embed = {
		key: "msl-alternate-calendar",
		type: "inline_svg",
		content: `<svg width="21" height="21" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 24 24"><path fill="currentColor" d="M5.615 21q-.69 0-1.152-.462Q4 20.075 4 19.385V6.615q0-.69.463-1.152Q4.925 5 5.615 5h1.77V3.308q0-.233.153-.386q.152-.153.385-.153t.386.153q.153.153.153.386V5h7.153V3.27q0-.214.144-.357t.356-.144q.214 0 .357.143t.143.357V5h1.77q.69 0 1.152.463q.463.462.463 1.152v12.77q0 .69-.462 1.152q-.463.463-1.153.463zm0-1h12.77q.23 0 .423-.192q.192-.193.192-.423v-8.77H5v8.77q0 .23.192.423q.193.192.423.192M5 9.615h14v-3q0-.23-.192-.423Q18.615 6 18.385 6H5.615q-.23 0-.423.192Q5 6.385 5 6.615zm0 0V6zm7 4.539q-.31 0-.54-.23q-.23-.23-.23-.54q0-.309.23-.539q.23-.23.54-.23q.31 0 .54.23q.23.23.23.54q0 .31-.23.539q-.23.23-.54.23m-4 0q-.31 0-.54-.23q-.23-.23-.23-.54q0-.309.23-.539q.23-.23.54-.23q.31 0 .54.23q.23.23.23.54q0 .31-.23.539q-.23.23-.54.23m8 0q-.31 0-.54-.23q-.23-.23-.23-.54q0-.309.23-.539q.23-.23.54-.23q.31 0 .54.23q.23.23.23.54q0 .31-.23.539q-.23.23-.54.23M12 18q-.31 0-.54-.23q-.23-.23-.23-.54q0-.309.23-.539q.23-.23.54-.23q.31 0 .54.23q.23.23.23.54q0 .31-.23.54Q12.31 18 12 18m-4 0q-.31 0-.54-.23q-.23-.23-.23-.54q0-.309.23-.539q.23-.23.54-.23q.31 0 .54.23q.23.23.23.54q0 .31-.23.54Q8.31 18 8 18m8 0q-.31 0-.54-.23q-.23-.23-.23-.54q0-.309.23-.539q.23-.23.54-.23q.31 0 .54.23q.23.23.23.54q0 .31-.23.54Q16.31 18 16 18" stroke-width=".2" stroke="currentColor"/></svg>`
	};

	const styling = getContext<Styling>("styling");
	const device = getContext<string>("device");
	const iframe = getContext<boolean>("iframe");
	const formLanguage = getContext<Language>("formLanguage");

	let internalValue: string;
	const dispatch = createEventDispatcher();
	let inputRef: HTMLInputElement;
	let datepickerRef: HTMLDivElement;
	let buttonRef: HTMLButtonElement;
	let mask: any;
	let is_open: boolean = false;
	let cleanup: CallbackFn | undefined;
	let deep_shadow: boolean = false;
	let is_empty: boolean = true;

	// const reverse = BROWSER && Boolean(navigator.language === "en-US");
	const reverse = field.settings.format === "mm/dd/yyyy";

	$: {
		if (typeof internalValue === "undefined" && value) {
			let date = dayjs(value).toDate();
			if (date instanceof Date && !isNaN(date.getTime())) {
				internalValue = dateToFormat(date);
				value = dateToISO(date);
			}
		}
	}

	$: {
		// When the field is updated, update the value of the input
		if (mask && value) {
			is_empty = internalValue === "";
			const maskValue = dateToISO(formatToDate(mask._value));
			if (maskValue !== value) {
				let date = dayjs(value).toDate();
				if (date instanceof Date && !isNaN(date.getTime())) {
					inputRef.value = dateToFormat(date);
					mask.value = dateToFormat(date);
					mask.updateValue();
				}
			}
		}
	}

	$: {
		if (is_open && datepickerRef && typeof cleanup === "undefined") {
			cleanup = autoUpdate(inputRef, datepickerRef, () => {
				update();
			});
		}
		if (!is_open && typeof cleanup !== "undefined") {
			cleanup();
			cleanup = undefined;
		}
	}

	const update = async () => {
		if (!datepickerRef || !is_open) return;
		const { x, y } = await computePosition(inputRef, datepickerRef, {
			placement: "bottom-start",
			middleware: [offset(6), flip(), shift({ padding: 5 })]
		});
		if (window.innerHeight <= 360 && iframe === true) {
			deep_shadow = true;
			Object.assign(datepickerRef.style, {
				left: `50%`,
				top: `50%`,
				transform: `translate(-50%, -50%)`
			});
		} else {
			Object.assign(datepickerRef.style, {
				left: `${x}px`,
				top: `${y}px`
			});
		}
	};

	$: {
		if (is_open) {
			requestAnimationFrame(update);
		}
	}

	/**
	 * Convert a date string in format "dd/mm/yyyy" or "mm/dd/yyyy" to a Date object.
	 * @param {string} str - The date string.
	 * @returns {Date} The Date object representing the input date string.
	 */
	const formatToDate = (str: string): Date => {
		let yearMonthDay = str
			.replace(/\s/g, "")
			.split("/")
			.map((x) => parseInt(x));
		if (reverse) {
			return new Date(yearMonthDay[2], yearMonthDay[0] - 1, yearMonthDay[1]);
		}
		return new Date(yearMonthDay[2], yearMonthDay[1] - 1, yearMonthDay[0]);
	};

	/**
	 * Convert a Date object to a formatted date string in "dd/mm/yyyy" or "mm/dd/yyyy" format.
	 * @param {Date} date - The Date object to convert.
	 * @returns {string} The formatted date string.
	 */
	const dateToFormat = (date: Date): string => {
		let day = date.getDate().toString().padStart(2, "0");
		let month = (date.getMonth() + 1).toString().padStart(2, "0");
		if (reverse) {
			return [month, day, date.getFullYear()].join("/");
		}
		return [day, month, date.getFullYear()].join("/");
	};

	/**
	 * Convert a Date object to an ISO formatted date string in "yyyy-mm-dd" format.
	 * @param {Date} date - The Date object to convert.
	 * @returns {string} The ISO formatted date string.
	 */
	const dateToISO = (date: Date): string => {
		let day = date.getDate().toString().padStart(2, "0");
		let month = (date.getMonth() + 1).toString().padStart(2, "0");
		return [date.getFullYear(), month, day].join("-");
	};

	onDestroy(() => {
		if (mask) mask.destroy();
		mask = undefined;
	});

	const dateSelect = (e: any) => {
		if (!e.detail) return;
		value = e.detail;
		let date = dayjs(value).toDate();
		if (date instanceof Date) {
			internalValue = dateToFormat(date);
		}
		is_open = false;
		dispatch("updateOther", {
			tainted: true
		});
	};

	const keyboardEventListeners = (e: KeyboardEvent) => {
		if (e.key === "Tab" && is_open) {
			is_open = false;
		}
	};

	onDestroy(() => {
		if (!BROWSER) return;
		document.removeEventListener("keydown", keyboardEventListeners);
	});

	onMount(async () => {
		if (!BROWSER) return;
		document.addEventListener("keydown", keyboardEventListeners);
		if (device !== "desktop") {
			inputRef.addEventListener("mousedown", (e) => {
				e.preventDefault();
				dispatch("updateOther", {
					active: true
				});
				is_open = true;
			});
		}
		inputRef.addEventListener("focus", (e) => {
			dispatch("updateOther", {
				active: true
			});
			is_open = true;
		});
		inputRef.addEventListener("blur", () => {
			dispatch("updateOther", {
				tainted: true
			});
			dispatch("updateOther", {
				active: false
			});
		});
		mask = IMask(inputRef, {
			mask: Date,
			pattern: reverse ? "MM/`DD/`YYYY" : "DD/`MM/`YYYY",
			eager: false,
			lazy: false,
			overwrite: true,
			min: new Date(1000, 0, 1),
			max: new Date(9999, 0, 1),
			blocks: {
				DD: {
					mask: IMask.MaskedRange,
					placeholderChar: "d",
					from: 1,
					to: 31,
					maxLength: 2,
					autofix: "pad"
				},
				MM: {
					mask: IMask.MaskedRange,
					from: 1,
					to: 12,
					placeholderChar: "m",
					maxLength: 2,
					autofix: "pad"
				},
				YYYY: {
					mask: IMask.MaskedRange,
					from: 0,
					to: 9999,
					placeholderChar: "y",
					maxLength: 4,
					autofix: "pad"
				}
			},
			format: (date: any): string => {
				return dateToFormat(date);
			},
			parse: (str: string): Date => {
				return formatToDate(str);
			}
		});
		mask.on("accept", () => {
			value = Boolean(mask.typedValue)
				? dateToISO(formatToDate(mask.value))
				: mask.value;
		});
	});
	function windowClick(e) {
		const ignore = [datepickerRef, inputRef, buttonRef];
		if (!is_open) return;
		if (ignore.some((el) => el && el.contains(e.target))) return;
		is_open = false;
	}
</script>

<svelte:window on:mousedown="{windowClick}" />

<div
	class="field-label-cover"
	class:has-placeholder="{styling.placeholders !== false}">
	{#if field.labelHTMLComputed && field.settings.hideLabel !== true}
		{#if styling.placeholders === false}
			<Label html="{field.labelHTMLComputed}" />
		{/if}
		{#if field.descriptionHTMLComputed}
			<Description html="{field.descriptionHTMLComputed}" />
		{/if}
	{/if}
</div>
<div class="field-input-cover">
	<div style="display: flex; align-items: center">
		<Input
			suffixIcon="{suffixIcon}"
			bind:element="{inputRef}"
			bind:value="{internalValue}"
			class="{is_empty ? 'empty' : ''}"
			inputmode="numeric"
			name="{field.id}"
			label="{field.labelComputed}"
			type="text" />
		{#await import("$basic/Datepicker.svelte") then Datepicker}
			{#if is_open}
				{#if deep_shadow}
					<div use:portal="{'body'}" class="deep-shadow-portal"></div>
				{/if}
				<div
					use:portal="{'body'}"
					class="form-popup shadow-dropdown"
					style="font-size: {deep_shadow
						? '1.10rem'
						: '1rem'}; position: {deep_shadow ? 'fixed' : 'absolute'}"
					class:shadow-dropdown-dark="{deep_shadow}"
					bind:this="{datepickerRef}">
					<svelte:component
						this="{Datepicker.default}"
						min="{field.settings.minComputed}"
						max="{field.settings.maxComputed}"
						allowedDays="{field.settings.allowedDays}"
						locale="{formLanguage.datepicker}"
						on:change="{dateSelect}"
						value="{value}" />
				</div>
			{/if}
		{/await}
		<!-- <button
			tabindex="-1"
			bind:this="{buttonRef}"
			type="button"
			style="opacity: {is_open ? '1' : '0.75'}"
			aria-label="Open datepicker"
			class="field-icon-button"
			on:click="{() => (is_open = true)}">
			{@html icons.datepicker}
		</button> -->
	</div>
	<FieldError
		visible="{field.tainted}"
		valid="{field.valid}"
		errorNumber="{field.errorNumber}"
		errorType="{field.errorType}" />
</div>
